import { useEffect, useState } from 'react';
import {
  Box, Avatar, Typography, Rating,
} from '@mui/material';
import {
  Facebook, Instagram, LinkedIn, Twitter, YouTube, Web, ArrowForward, Language, CenterFocusStrong,
} from '@mui/icons-material';
import {
  theme,
  loadMedia,
  getReaderCardFocus,
  getSocialMedia,
  getRating,
  getLanguages,
  translationMap,
  mapBackendKeyToStateKey,
} from '../../lib/index';
import { Btn } from '../Btn';
import { AudioPlayer } from '../AudioPlayer';
import { Loading } from '../Loading';
import { AudioBox } from './AudioBox';
import { AudioSocialContainer } from './AudioSocialContainer';
import { BottomBox } from './BottomBox';
import { Description } from './Description';
import { DividerHorizontal } from './DividerHorizontal';
import { FactsBigScreen } from './FactsBigScreen';
import { FactsMobile } from './FactsMobile';
import { Flexbox } from './Flexbox';
import { MoreInfoButton } from './MoreInfoButton';
import { ReaderContainer } from './ReaderContainer';
import { SocialIcon } from './SocialIcon';
import { Stats } from './Stats';
import { StyledLink } from './StyledLink';

function ReaderCard({ data }) {
  const [image, setImage] = useState('');
  const [audio, setAudio] = useState('');
  const userId = data?.id;
  const name = data?.acf.reader_profile_description_fullname || '';
  const location = data?.acf.reader_profile_description_location || '';
  const description = data?.acf.reader_profile_description_introduction || '';
  const ratingCount = data?.acf.reader_rating ? data.acf.reader_rating.length : [];
  const rating = data ? getRating(data) : -1;
  const focus = data ? getReaderCardFocus(data) : [];
  const socialMedia = data && getSocialMedia(data);

  // map data to screen
  useEffect(() => {
    if (data) {
      if (data.acf.reader_profile_description_photo) {
        loadMedia(data.acf.reader_profile_description_photo).then(setImage);
      }

      if (data.acf.reader_media_audiosample) {
        loadMedia(data.acf.reader_media_audiosample).then(setAudio);
      }

      getLanguages(data);
    }
  }, [data]);

  if (!data) {
    return (
      <Loading>
        <Box>
          {name}
          {location}
          {rating}
        </Box>
      </Loading>
    );
  }

  const languages = data ? getLanguages(data) : [];
  return (
    <ReaderContainer>

      {/* mobile view */}
      <FactsMobile to={`/?coisar_reader=${userId}`}>
        <Avatar src={image.src} />
        <Typography className="readerName" variant="h5">{name}</Typography>
        <Typography className="readerLocation">{location}</Typography>
        <Stats>
          { rating
            ? (
              <>
                <Rating name="rating" sx={{ color: theme.palette.yellowOne }} value={parseFloat(rating)} precision={0.1} readOnly />
                <Typography>
                  (
                  {ratingCount}
                  )
                </Typography>
              </>
            )
            : <Rating name="rating" sx={{ color: theme.palette.yellowOne }} value={0} precision={0.1} readOnly />}
        </Stats>
        { languages.length !== 0
      && (
      <>
        <Typography className="headline" variant="h6">
          {languages.length === 1
            ? 'Sprache'
            : 'Sprachen'}
        </Typography>
        <Typography className="text">{languages.map((e) => mapBackendKeyToStateKey(e, translationMap)).join(', ')}</Typography>
      </>
      )}
        { focus.length !== 0
      && (
      <>
        <Typography className="headline" variant="h6">
          { focus.length === 1
            ? 'Spezialgebiet'
            : 'Spezialgebiete'}
        </Typography>
        <Typography className="text">{focus.join(', ')}</Typography>
      </>
      )}
        <MoreInfoButton endIcon={<ArrowForward />} variant="contained">weitere Informationen</MoreInfoButton>
      </FactsMobile>

      {/* desktop view */}
      <FactsBigScreen>
        <Avatar src={image.src} />
        <Box sx={{ height: 390 }}>
          <Typography variant="h5" sx={{ marginBottom: '10px', height: 30 }}>{name}</Typography>
          <Typography variant="p" sx={{ height: 24 }}>{location}</Typography>
          <Stats>
            { rating
              ? (
                <>
                  <Rating name="rating" sx={{ color: theme.palette.yellowOne }} value={parseFloat(rating)} precision={0.1} readOnly />
                  <Typography>
                    (
                    {ratingCount}
                    )
                  </Typography>
                </>
              )
              : <Rating name="rating" sx={{ color: theme.palette.yellowOne }} value={0} precision={0.1} readOnly />}
          </Stats>
          <Description>{description}</Description>
          { languages.length !== 0
          && (
          <Flexbox>
            <Language />
            <Typography>{languages.map((e) => mapBackendKeyToStateKey(e, translationMap)).join(', ')}</Typography>
          </Flexbox>
          )}
          { focus.length !== 0
          && (
          <Flexbox>
            <CenterFocusStrong />
            <Typography>{focus.join(', ')}</Typography>
          </Flexbox>
          )}
        </Box>
        <BottomBox>
          <DividerHorizontal />
          <AudioSocialContainer>
            { audio && (audio.src.includes('mp3') || audio.src.includes('wav'))
            && (
            <AudioBox>
              <AudioPlayer src={audio.src} />
            </AudioBox>
            )}
            <Box>
              {socialMedia.length > 0
                ? socialMedia.map((e) => (
                  <SocialIcon
                    key={e}
                    href={
                      e.indexOf('https://') || e.indexOf('http://')
                        ? e
                        : `https://${e}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {
                      // @TODO: Refactor this into a function
                      /* eslint-disable */
                      e.indexOf('twitter') !== -1
                        ? <Twitter />
                        : e.indexOf('facebook') !== -1
                          ? <Facebook />
                          : e.indexOf('instagram') !== -1
                            ? <Instagram />
                            : e.indexOf('youtube') !== -1
                              ? <YouTube />
                              : e.indexOf('linkedin') !== -1
                                ? <LinkedIn />
                                : <Web />
                      /* eslint-enable */
                    }
                  </SocialIcon>
                ))
                : null}
            </Box>
          </AudioSocialContainer>
          <StyledLink to={`/?coisar_reader=${userId}`}>
            <Btn buttonName="weitere Informationen" />
          </StyledLink>
        </BottomBox>
      </FactsBigScreen>
    </ReaderContainer>
  );
}

export default ReaderCard;
